// Single-source live state for an Operation detail page.
//
// Why one query owns both the seed and the live deltas: previously the
// page had two writers competing on the same React state — a 2s
// `refetchInterval` poll that overwrote `steps` from the persisted DB row,
// and an `EventSource` that pushed `event:step` deltas on top of it. The
// DB snapshot lagged (the writer had a leading-edge throttle with no
// trailing flush) so the poll routinely clobbered fresh SSE state with
// stale data. We collapse both writers into a single TanStack Query cache
// entry: the parent passes the cold-start snapshot as `initialValue`, the
// stream merges deltas via a pure reducer, and there is exactly one place
// in the React tree that owns `{ steps, log, status, exitCode, finishedAt }`.
//
// We use `@microsoft/fetch-event-source` instead of the native EventSource
// because it supports custom headers/credentials cleanly, exposes `Last-Event-ID`
// reconnect, and lets us bridge the callback-based stream into an
// AsyncIterable that `experimental_streamedQuery` can drive.

import {
	EventStreamContentType,
	fetchEventSource,
} from "@microsoft/fetch-event-source";
import {
	experimental_streamedQuery as streamedQuery,
	useQuery,
} from "@tanstack/react-query";
import { useCallback, useRef } from "react";
import type { TimelineStep } from "@/components/operation-step-timeline";

export interface LiveState {
	status: string;
	exitCode: number | null;
	finishedAt: string | null;
	steps: TimelineStep[];
	log: string;
	/**
	 * True once the backend emitted `event: end` (or the stream closed
	 * cleanly). UI uses this to decide whether to fetch the on-disk log
	 * for very-old terminal ops whose `logTail` was already truncated.
	 */
	streamClosed: boolean;
}

export type SseEvent =
	| { type: "progress"; steps: TimelineStep[] }
	| { type: "step"; step: TimelineStep }
	| { type: "log"; text: string }
	| { type: "status"; status: string; exitCode: number | null }
	| { type: "end" }
	| { type: "error"; message?: string };

/**
 * Pure reducer. No React, no fetch, no time. Easy to unit test and easy
 * to reason about: every SSE message becomes one immutable next state.
 *
 * Step merge rule mirrors the previous inline implementation in
 * OperationDetail: index by `step` key, append on first-seen, shallow
 * merge in place on subsequent updates. Re-applying the same payload is
 * idempotent (deep-equal), which is what TanStack Query needs for its
 * "replace" refetch mode to be safe under reconnects.
 */
export function applyEvent(state: LiveState, ev: SseEvent): LiveState {
	switch (ev.type) {
		case "progress":
			return { ...state, steps: ev.steps };
		case "step": {
			const idx = state.steps.findIndex((s) => s.step === ev.step.step);
			if (idx < 0) return { ...state, steps: [...state.steps, ev.step] };
			const next = state.steps.slice();
			next[idx] = { ...next[idx], ...ev.step };
			return { ...state, steps: next };
		}
		case "log":
			return { ...state, log: state.log + ev.text };
		case "status":
			return {
				...state,
				status: ev.status,
				exitCode: ev.exitCode,
				// Backend's `status` event doesn't carry the terminal
				// timestamp. Stamp it client-side as a best-effort so the
				// "Finished" metadata row updates immediately; a follow-up
				// metadata refetch (if the parent decides to do one) will
				// reconcile with the authoritative DB value.
				finishedAt: state.finishedAt ?? new Date().toISOString(),
			};
		case "end":
			return { ...state, streamClosed: true };
		case "error":
			// Non-fatal notifications from the backend. The query's own
			// error channel surfaces fatal stream errors; we deliberately
			// don't mutate state here to keep `error` events from racing
			// against the next real message.
			return state;
	}
}

/**
 * Minimal async queue: push/close pattern that adapts the callback-based
 * `fetchEventSource` API into an AsyncIterable for `streamedQuery`.
 *
 * Implementation detail: we keep a single pending resolver plus a
 * backlog array. Backlog drains FIFO, then `next()` parks on a fresh
 * promise until the next `push` (or `close`).
 */
class AsyncQueue<T> {
	private backlog: T[] = [];
	private waiter: ((v: IteratorResult<T>) => void) | null = null;
	private done = false;

	push(value: T): void {
		if (this.done) return;
		if (this.waiter) {
			const w = this.waiter;
			this.waiter = null;
			w({ value, done: false });
			return;
		}
		this.backlog.push(value);
	}

	close(): void {
		if (this.done) return;
		this.done = true;
		if (this.waiter) {
			const w = this.waiter;
			this.waiter = null;
			w({ value: undefined as unknown as T, done: true });
		}
	}

	[Symbol.asyncIterator](): AsyncIterator<T> {
		return {
			next: (): Promise<IteratorResult<T>> => {
				if (this.backlog.length > 0) {
					return Promise.resolve({
						value: this.backlog.shift() as T,
						done: false,
					});
				}
				if (this.done) {
					return Promise.resolve({
						value: undefined as unknown as T,
						done: true,
					});
				}
				return new Promise<IteratorResult<T>>((resolve) => {
					this.waiter = resolve;
				});
			},
			return: (): Promise<IteratorResult<T>> => {
				this.close();
				return Promise.resolve({
					value: undefined as unknown as T,
					done: true,
				});
			},
		};
	}
}

/** Parse one SSE message into our discriminated union, or null if junk. */
function parse(ev: { event: string; data: string }): SseEvent | null {
	switch (ev.event) {
		case "progress": {
			try {
				const arr = JSON.parse(ev.data);
				if (!Array.isArray(arr)) return null;
				return { type: "progress", steps: arr as TimelineStep[] };
			} catch {
				return null;
			}
		}
		case "step": {
			try {
				const obj = JSON.parse(ev.data) as TimelineStep & { event?: string };
				// Strip the discriminator the backend includes inside the
				// JSON payload — it overlaps the SSE-level `event:` name
				// and would only confuse downstream consumers.
				const { event: _evt, ...step } = obj;
				void _evt;
				return { type: "step", step: step as TimelineStep };
			} catch {
				return null;
			}
		}
		case "log":
			// Backend deliberately sends raw text (not JSON) for log
			// chunks so high-volume streams don't pay JSON-encoding cost.
			return { type: "log", text: ev.data };
		case "status": {
			try {
				const obj = JSON.parse(ev.data) as {
					status: string;
					exitCode: number | null;
				};
				return {
					type: "status",
					status: obj.status,
					exitCode: obj.exitCode,
				};
			} catch {
				return null;
			}
		}
		case "end":
			return { type: "end" };
		case "error": {
			try {
				const obj = JSON.parse(ev.data) as { message?: string };
				return { type: "error", message: obj.message };
			} catch {
				return { type: "error" };
			}
		}
		default:
			// Unknown event name — drop instead of throwing so a future
			// backend addition doesn't take the whole stream down.
			return null;
	}
}

/**
 * Marker error: thrown from `onerror` to signal `fetchEventSource` should
 * stop retrying. The library treats anything thrown from `onerror` as
 * fatal (vs. returning `number | void` for "retry after N ms").
 */
class FatalStreamError extends Error {}

async function* sseEvents(
	id: string,
	signal: AbortSignal,
): AsyncGenerator<SseEvent> {
	const queue = new AsyncQueue<SseEvent>();
	const url = `/api/operations/${encodeURIComponent(id)}/log/stream`;

	// fetchEventSource returns a promise that resolves when the stream
	// ends (clean close, abort, or fatal error). We fire-and-forget here
	// and let queue.close() drive the async iteration to completion.
	void fetchEventSource(url, {
		signal,
		credentials: "include",
		openWhenHidden: true,
		async onopen(res) {
			if (
				res.ok &&
				res.headers.get("content-type")?.includes(EventStreamContentType)
			) {
				return;
			}
			// 4xx (auth) or wrong content-type: refuse to retry. 5xx is
			// treated as transient by the library's default behavior.
			if (res.status >= 400 && res.status < 500) {
				throw new FatalStreamError(`stream rejected: ${res.status}`);
			}
			throw new Error(`stream unavailable: ${res.status}`);
		},
		onmessage(ev) {
			const parsed = parse(ev);
			if (parsed) queue.push(parsed);
		},
		onclose() {
			queue.close();
		},
		onerror(err) {
			if (err instanceof FatalStreamError) {
				queue.close();
				throw err;
			}
			// Light retry on transient errors. The library handles the
			// actual reconnect; we just return the delay.
			return 2_000;
		},
	}).catch(() => {
		// Swallow — the queue is already closed by onclose/onerror and
		// the consumer (streamedQuery) sees the iteration end naturally.
		queue.close();
	});

	signal.addEventListener("abort", () => queue.close(), { once: true });

	yield* queue;
}

/**
 * Pass-through async generator that invokes the supplied callback when the
 * terminal `end` event flows past, then yields it unchanged. Keeps the
 * side effect on the stream path while the reducer stays pure. The callback
 * is read from a ref so its identity can change without re-creating the
 * stream.
 */
async function* tapEnd(
	source: AsyncGenerator<SseEvent>,
	onEndRef: { current: (() => void) | undefined },
): AsyncGenerator<SseEvent> {
	for await (const ev of source) {
		if (ev.type === "end") onEndRef.current?.();
		yield ev;
	}
}

export interface UseOperationLiveResult {
	data: LiveState;
	isFetching: boolean;
	error: Error | null;
	cancel: () => Promise<void>;
}

/**
 * Subscribe to the live event stream for an operation. The `initial`
 * argument seeds the cache so the first paint shows the cold-start
 * snapshot from `GET /api/operations/:id` instead of an empty timeline.
 *
 * For terminal operations the backend immediately sends a full progress
 * snapshot + `status` + `end` and closes the connection, so this hook is
 * cheap to mount even on long-finished rows.
 */
export interface UseOperationLiveOptions {
	/**
	 * Fired once when the backend closes the stream (`event: end`), which
	 * always follows the terminal `status` event. Use this to react to an
	 * operation finishing (e.g. invalidate dependent queries) from the
	 * stream's terminal-event path rather than a status-watching effect.
	 * Held in a ref so a changing callback identity never re-opens the
	 * stream.
	 */
	onEnd?: () => void;
}

export function useOperationLive(
	id: string,
	initial: LiveState,
	options?: UseOperationLiveOptions,
): UseOperationLiveResult {
	const onEndRef = useRef(options?.onEnd);
	onEndRef.current = options?.onEnd;

	const query = useQuery({
		queryKey: ["operation", id, "live"],
		queryFn: streamedQuery({
			streamFn: ({ signal }) => tapEnd(sseEvents(id, signal), onEndRef),
			initialValue: initial,
			reducer: (state: LiveState, ev: SseEvent) => applyEvent(state, ev),
			// "replace" so a reconnect refetch atomically swaps the
			// accumulated state in instead of double-counting log lines
			// from the replayed snapshot.
			refetchMode: "replace",
		}),
		// The stream is the freshness signal; we never want TanStack to
		// background-refetch (that would tear down the live connection
		// and reopen mid-page, doubling the log buffer).
		staleTime: Number.POSITIVE_INFINITY,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});

	const cancel = useCallback(async () => {
		const res = await fetch(
			`/api/operations/${encodeURIComponent(id)}/cancel`,
			{ method: "POST", credentials: "include" },
		);
		if (!res.ok) {
			let msg = `cancel failed: ${res.status}`;
			try {
				const body = (await res.json()) as {
					error?: { message?: string };
				};
				if (body?.error?.message) msg = body.error.message;
			} catch {}
			throw new Error(msg);
		}
	}, [id]);

	return {
		data: (query.data ?? initial) as LiveState,
		isFetching: query.isFetching,
		error: query.error as Error | null,
		cancel,
	};
}
